import { useEffect, useRef, useState } from "react";
import InputIconSearchPanelMultiSelection from "./InputIconSearchPanelMultiSelection";
import Tag from "./Tag";
import ButtonIconH from "./ButtonIconH";
import { userData, inputOnEnter } from "../js/utils";
import { post } from "../js/post";
import moment from "moment";
import DataTable from "react-data-table-component";
import {
  storeDashboardBrandTable,
  storeDashboardFamilyTable,
  storeDashboardSupplierTable,
} from "../js/tableColumns";
import { useOptics } from "../hooks/useOptics";
import DateInput from "./DateInput";

const STATS_TABS = ["Familias", "Proveedores", "Marcas"];

const STATS_ENDPOINTS = {
  Familias:
    "support-extra/dashboard/ObtenerEstadisticasAlmacenFamiliasDashboard.php",
  Proveedores:
    "support-extra/dashboard/ObtenerEstadisticasAlmacenProveedoresDashboard.php",
  Marcas:
    "support-extra/dashboard/ObtenerEstadisticasAlmacenMarcasDashboard.php",
};

const SUM_FIELDS = [
  "volVentas",
  "ventasNetas",
  "comprasNetas",
  "udsCompradas",
  "udsVendidas",
  "margen",
];
const AVG_FIELDS = ["dto", "mcc", "mcv"];
const round2 = (value) => Math.round(value * 100) / 100;

// Combina filas: suma los importes/unidades y promedia los porcentajes
function combineRows(rows, familia) {
  const combined = { familia };
  SUM_FIELDS.forEach((field) => {
    combined[field] = round2(
      rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0),
    );
  });
  AVG_FIELDS.forEach((field) => {
    combined[field] = rows.length
      ? round2(
          rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0) /
            rows.length,
        )
      : 0;
  });
  return combined;
}

// Resumen global calculado a partir de las tablas de las ópticas mostradas.
// Solo tiene sentido con más de una óptica con datos
function getFamiliesGlobalSummary(datosOpticas) {
  const opticsWithData = datosOpticas.filter((e) => e.datos.length > 0);
  if (opticsWithData.length < 2) return null;
  const rowsByFamily = new Map();
  opticsWithData.forEach((optic) => {
    optic.datos.forEach((row) => {
      if (!rowsByFamily.has(row.familia)) rowsByFamily.set(row.familia, []);
      rowsByFamily.get(row.familia).push(row);
    });
  });

  return {
    datos: [...rowsByFamily].map(([familia, rows]) =>
      combineRows(rows, familia),
    ),
    totales: combineRows(
      opticsWithData.map((e) => e.totales),
      "Total Global",
    ),
  };
}

export function DashboardStoreStatsPanel() {
  moment.locale("es");
  const [statsTabSelected, setStatsTabSelected] = useState("Familias");
  const opticalList = useOptics();
  // Filtros compartidos por Familias, Proveedores y Marcas
  const [selectedOpticalList, setSelectedOpticalList] = useState([]);
  const [searchYear, setSearchYear] = useState(moment());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const clearFilters = () => {
    setSelectedOpticalList([]);
    setSearchYear(moment());
    setStartDate("");
    setEndDate("");
  };

  const filters = {
    opticas: opticalList
      .filter((e) => selectedOpticalList.includes(e.text))
      .map((e) => e.id),
    ano: searchYear.year(),
    fechaDesde: startDate,
    fechaHasta: endDate,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className="opt-table-content grid-13" id="dashboard-store-stats">
        <div className="cell cell-start-content">Ópticas</div>
        <div className="cell">
          <InputIconSearchPanelMultiSelection
            size={0}
            tabIndex={10}
            type={"type-alphanum-ext"}
            options={opticalList.map((e) => e.text)}
            selectedOptions={selectedOpticalList}
            onChange={(e) => {
              setSelectedOpticalList(e);
            }}
          ></InputIconSearchPanelMultiSelection>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Año</div>
        <div className="cell">
          <div className="data-field">
            <button
              className="incdec-btn"
              onClick={() => {
                setSearchYear((prev) => moment(prev).subtract(1, "year"));
              }}
            >
              {"<"}
            </button>
            <span style={{ width: "35px", textAlign: "center" }}>
              {moment(searchYear).year()}
            </span>
            <button
              className="incdec-btn"
              onClick={() => {
                setSearchYear((prev) => moment(prev).add(1, "year"));
              }}
            >
              {">"}
            </button>
          </div>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Fecha desde</div>
        <div className="cell">
          <DateInput
            max="9999-12-31"
            tabIndex={11}
            className="input-field"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
            onKeyUp={(e) => inputOnEnter(e)}
          />
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Fecha hasta</div>
        <div className="cell">
          <DateInput
            max="9999-12-31"
            tabIndex={12}
            className="input-field"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
            }}
            onKeyUp={(e) => inputOnEnter(e)}
          />
        </div>
        <div className="cell combined-cells-2 cell-end-content">
          <button className="cell-label" onClick={clearFilters} tabIndex={-1}>
            Limpiar filtros
          </button>
        </div>
        {selectedOpticalList.length > 0 && (
          <div className="cell combined-cells-13 cell-start-content">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Ópticas seleccionadas: {selectedOpticalList.length}
              </span>
              <div className="tag-content">
                {selectedOpticalList.map((e, i) => {
                  return (
                    <Tag
                      text={e}
                      key={i}
                      tagId={i}
                      onClick={(e) => {
                        setSelectedOpticalList((prev) =>
                          prev.filter((element) => element !== e),
                        );
                      }}
                    ></Tag>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: 10,
          marginTop: 28,
          marginBottom: 40,
        }}
      >
        {STATS_TABS.map((tab) => (
          <ButtonIconH
            key={tab}
            text={tab}
            selected={statsTabSelected === tab}
            onClick={() => {
              setStatsTabSelected(tab);
            }}
          ></ButtonIconH>
        ))}
      </div>
      {/* Las tres secciones permanecen montadas para conservar sus datos */}
      {STATS_TABS.map((tab) => (
        <div
          key={tab}
          style={{ display: statsTabSelected === tab ? "block" : "none" }}
        >
          <DashboardStoreStatsResults
            tab={tab}
            active={statsTabSelected === tab}
            filters={filters}
          />
        </div>
      ))}
    </div>
  );
}

// Resultados de una sección (Familias, Proveedores o Marcas) con los filtros
// compartidos. Solo pide datos estando visible y si los filtros han cambiado
// desde la última carga
function DashboardStoreStatsResults({ tab, active, filters }) {
  const [statsData, setStatsData] = useState(null);
  const loadedFiltersKey = useRef(null);
  const lastRequestId = useRef(0);
  const filtersKey = JSON.stringify(filters);

  const getStatsData = async () => {
    const requestId = ++lastRequestId.current;
    const response = await post(STATS_ENDPOINTS[tab], {
      token: userData.token,
      ...filters,
      tipoFichero: "",
    });
    if (requestId === lastRequestId.current) setStatsData(response);
  };

  useEffect(() => {
    if (!active || loadedFiltersKey.current === filtersKey) return;
    loadedFiltersKey.current = filtersKey;
    if (filters.opticas.length > 0) getStatsData();
    else {
      lastRequestId.current++;
      setStatsData(null);
    }
  }, [active, filtersKey]);

  const familiesGlobalSummary =
    tab === "Familias" && statsData?.datosOpticas
      ? getFamiliesGlobalSummary(statsData.datosOpticas)
      : null;

  if (filters.opticas.length === 0) {
    return (
      <div>
        <div
          style={{
            padding: "10px 0",
            borderTop: "1px solid #d6d6d6",
            width: "100%",
          }}
        >
          <DataTable
            columns={[]}
            data={[]}
            className="custom-datatable dashboard-custom-table non-interactive"
            noDataComponent="Selecciona al menos una óptica para mostrar datos."
          />
        </div>
        <ExportButton />
      </div>
    );
  }

  return (
    <div>
      {tab === "Familias" && (
        <div>
          {statsData !== null &&
            statsData.datosOpticas.map((e) => {
              return (
                <div
                  style={{
                    padding: "10px 0",
                    display: "flex",
                    flexDirection: "column",
                    borderTop: "1px solid #d6d6d6",
                    width: "100%",
                    gap: 10,
                  }}
                  key={e.idOptica}
                >
                  <span className="dashboard-section-or-title">
                    {e.idOptica} - {e.nombreOptica}
                  </span>
                  <>
                    <DataTable
                      columns={storeDashboardFamilyTable}
                      data={e.datos}
                      className="custom-datatable dashboard-custom-table non-interactive"
                      noDataComponent="No hay datos para esta óptica."
                    />
                    {e.datos.length > 0 && (
                      <DataTable
                        columns={storeDashboardFamilyTable}
                        data={[e.totales]}
                        className="custom-datatable summary-row no-header-datatable"
                      />
                    )}
                  </>
                </div>
              );
            })}
          {familiesGlobalSummary !== null && (
            <div
              style={{
                padding: "10px 0",
                display: "flex",
                flexDirection: "column",
                borderTop: "1px solid #d6d6d6",
                width: "100%",
                gap: 10,
              }}
            >
              <span className="dashboard-section-or-title">Resumen global</span>
              <>
                <DataTable
                  columns={storeDashboardFamilyTable}
                  data={familiesGlobalSummary.datos}
                  className="custom-datatable dashboard-custom-table non-interactive"
                  noDataComponent="No hay datos globales."
                />
                {familiesGlobalSummary.datos.length > 0 && (
                  <DataTable
                    columns={storeDashboardFamilyTable}
                    data={[familiesGlobalSummary.totales]}
                    className="custom-datatable summary-row no-header-datatable"
                  />
                )}
              </>
            </div>
          )}
        </div>
      )}
      {tab === "Proveedores" && (
        <div>
          {statsData !== null &&
            statsData.map((e, i) => {
              return (
                <div
                  style={{
                    padding: "10px 0",
                    display: "flex",
                    flexDirection: "column",
                    borderTop: "1px solid #d6d6d6",
                    width: "100%",
                    gap: 10,
                  }}
                  key={i}
                >
                  <span className="dashboard-section-or-title">
                    {e.familia}
                  </span>
                  <>
                    <DataTable
                      columns={storeDashboardSupplierTable}
                      data={e.datos}
                      className="custom-datatable dashboard-custom-table non-interactive"
                      noDataComponent="No hay datos para esta familia."
                    />
                    {e.datos.length > 0 && (
                      <DataTable
                        columns={storeDashboardSupplierTable}
                        data={[e.totales]}
                        className="custom-datatable summary-row no-header-datatable"
                      />
                    )}
                  </>
                </div>
              );
            })}
        </div>
      )}
      {tab === "Marcas" && (
        <div>
          {statsData !== null &&
            statsData.map((e, i) => {
              return (
                <div
                  style={{
                    padding: "10px 0",
                    display: "flex",
                    flexDirection: "column",
                    borderTop: "1px solid #d6d6d6",
                    width: "100%",
                    gap: 10,
                  }}
                  key={i}
                >
                  <span className="dashboard-section-or-title">
                    {e.familia}
                  </span>
                  <>
                    <DataTable
                      columns={storeDashboardBrandTable}
                      data={e.datos}
                      className="custom-datatable dashboard-custom-table non-interactive"
                      noDataComponent="No hay datos para esta familia."
                    />
                    {e.datos.length > 0 && (
                      <DataTable
                        columns={storeDashboardBrandTable}
                        data={[e.totales]}
                        className="custom-datatable summary-row no-header-datatable"
                      />
                    )}
                  </>
                </div>
              );
            })}
        </div>
      )}
      <ExportButton />
    </div>
  );
}

function ExportButton() {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        marginTop: 15,
      }}
    >
      <ButtonIconH
        text={"Exportar"}
        small={true}
        onClick={() => {}}
      ></ButtonIconH>
    </div>
  );
}
